export interface StoryboardMeta {
  title: string;
  audience: string;
  duration: string;
  narrative: string;
}

export interface SlideData {
  num: number;
  type: string;
  title: string;
  body: string[];
  notes: string[];
}

export interface StoryboardData {
  meta: StoryboardMeta;
  slides: SlideData[];
}

const HEADER_LINE = '=== STORYBOARD ===';
const SLIDE_SEPARATOR_RE = /^---\s*SLIDE\s+(\d+)\s+\[([^\]]+)\]\s*---$/;
const META_KEY_RE = /^(title|audience|duration|narrative):\s*(.*)$/;
const NOTE_PREFIX = 'NOTE: ';

function emptyMeta(): StoryboardMeta {
  return { title: '', audience: '', duration: '', narrative: '' };
}

function emptyData(): StoryboardData {
  return { meta: emptyMeta(), slides: [] };
}

function parseMeta(lines: string[]): StoryboardMeta {
  const meta = emptyMeta();
  for (const line of lines) {
    const m = line.match(META_KEY_RE);
    if (m) {
      const key = m[1] as keyof StoryboardMeta;
      meta[key] = m[2].trim();
    }
  }
  return meta;
}

function parseSlideBlock(headerLine: string, contentLines: string[]): SlideData {
  const m = headerLine.match(SLIDE_SEPARATOR_RE)!;
  const num = parseInt(m[1], 10);
  const type = m[2];

  // Find the title: first non-empty line
  let titleIdx = -1;
  for (let i = 0; i < contentLines.length; i++) {
    if (contentLines[i].trim() !== '') {
      titleIdx = i;
      break;
    }
  }

  if (titleIdx === -1) {
    return { num, type, title: '', body: [], notes: [] };
  }

  const title = contentLines[titleIdx];

  // Everything after the title (skipping the empty line that separates title from body)
  let bodyStart = titleIdx + 1;
  // Skip one empty line after title if present
  if (bodyStart < contentLines.length && contentLines[bodyStart].trim() === '') {
    bodyStart++;
  }

  const body: string[] = [];
  const notes: string[] = [];

  for (let i = bodyStart; i < contentLines.length; i++) {
    const line = contentLines[i];
    if (line.startsWith(NOTE_PREFIX)) {
      notes.push(line.slice(NOTE_PREFIX.length));
    } else {
      body.push(line);
    }
  }

  // Trim trailing empty lines from body
  while (body.length > 0 && body[body.length - 1].trim() === '') {
    body.pop();
  }

  return { num, type, title, body, notes };
}

export function parseStoryboard(raw: string): StoryboardData {
  if (!raw || raw.trim() === '') {
    return emptyData();
  }

  const lines = raw.split('\n');

  // Find the header line index
  let headerIdx = lines.findIndex((l) => l.trim() === HEADER_LINE);

  // Find all slide separator indices
  const slideSepIndices: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (SLIDE_SEPARATOR_RE.test(lines[i].trim())) {
      slideSepIndices.push(i);
    }
  }

  if (slideSepIndices.length === 0) {
    // No slides found at all
    if (headerIdx >= 0) {
      // We have a header but no slides
      const metaLines = lines.slice(headerIdx + 1);
      return { meta: parseMeta(metaLines), slides: [] };
    }
    return emptyData();
  }

  // Parse meta: lines between header and first slide separator
  let meta: StoryboardMeta;
  if (headerIdx >= 0 && headerIdx < slideSepIndices[0]) {
    const metaLines = lines.slice(headerIdx + 1, slideSepIndices[0]);
    meta = parseMeta(metaLines);
  } else {
    // No header — try to parse meta from lines before first slide
    const metaLines = lines.slice(0, slideSepIndices[0]);
    meta = parseMeta(metaLines);
  }

  // Parse each slide
  const slides: SlideData[] = [];
  for (let i = 0; i < slideSepIndices.length; i++) {
    const sepIdx = slideSepIndices[i];
    const nextSepIdx =
      i + 1 < slideSepIndices.length ? slideSepIndices[i + 1] : lines.length;
    const headerLine = lines[sepIdx].trim();
    const contentLines = lines.slice(sepIdx + 1, nextSepIdx);
    slides.push(parseSlideBlock(headerLine, contentLines));
  }

  return { meta, slides };
}

export function serializeStoryboard(data: StoryboardData): string {
  const parts: string[] = [];

  // Header
  parts.push(HEADER_LINE);
  if (data.meta.title) parts.push(`title: ${data.meta.title}`);
  if (data.meta.audience) parts.push(`audience: ${data.meta.audience}`);
  if (data.meta.duration) parts.push(`duration: ${data.meta.duration}`);
  if (data.meta.narrative) parts.push(`narrative: ${data.meta.narrative}`);

  // Slides
  for (const slide of data.slides) {
    parts.push('');
    parts.push(`--- SLIDE ${slide.num} [${slide.type}] ---`);
    parts.push(slide.title);

    if (slide.body.length > 0 || slide.notes.length > 0) {
      parts.push('');
      for (const line of slide.body) {
        parts.push(line);
      }
    }

    for (const note of slide.notes) {
      parts.push('');
      parts.push(`${NOTE_PREFIX}${note}`);
    }
  }

  // Trailing newline
  parts.push('');

  return parts.join('\n');
}

export function updateSlideInRaw(
  raw: string,
  slideNum: number,
  updated: Partial<SlideData>
): string {
  const lines = raw.split('\n');

  // Find the target slide separator
  let targetIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].trim().match(SLIDE_SEPARATOR_RE);
    if (m && parseInt(m[1], 10) === slideNum) {
      targetIdx = i;
      break;
    }
  }

  if (targetIdx === -1) {
    return raw;
  }

  // Find the end of this slide (next separator or end of file)
  let endIdx = lines.length;
  for (let i = targetIdx + 1; i < lines.length; i++) {
    if (SLIDE_SEPARATOR_RE.test(lines[i].trim())) {
      endIdx = i;
      break;
    }
  }

  // Parse the existing slide to get current values
  const headerLine = lines[targetIdx].trim();
  const contentLines = lines.slice(targetIdx + 1, endIdx);
  const existing = parseSlideBlock(headerLine, contentLines);

  // Merge updates
  const merged: SlideData = {
    num: updated.num ?? existing.num,
    type: updated.type ?? existing.type,
    title: updated.title ?? existing.title,
    body: updated.body ?? existing.body,
    notes: updated.notes ?? existing.notes,
  };

  // Rebuild the slide block
  const newSlideLines: string[] = [];
  newSlideLines.push(`--- SLIDE ${merged.num} [${merged.type}] ---`);
  newSlideLines.push(merged.title);

  if (merged.body.length > 0 || merged.notes.length > 0) {
    newSlideLines.push('');
    for (const line of merged.body) {
      newSlideLines.push(line);
    }
  }

  for (const note of merged.notes) {
    newSlideLines.push('');
    newSlideLines.push(`${NOTE_PREFIX}${note}`);
  }

  // Replace the old slide lines with new ones
  const before = lines.slice(0, targetIdx);
  const after = lines.slice(endIdx);

  return [...before, ...newSlideLines, ...after].join('\n');
}
